import { useCallback, useEffect, useMemo, useState } from 'react'
import { normalizeComplianceScope, normalizeComplianceStandardCode } from '../../utils/complianceStandard'
import { Link, useSearchParams } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { API_ENDPOINTS } from '../../config/api'
import { PageLayout } from '../../layouts/PageLayout'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { TableEmptyRow, TableLoadingRow } from '../../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

type RiskRow = {
  id: string
  title: string
  level: number
  standardCode?: string | null
  controlKey?: string | null
  status: string
  ownerName?: string | null
  riskOrigin?: string | null
  recommendationLinkageReason?: string | null
  recommendationId?: string | null
  vulnerabilityCause?: string | null
  requiredEvidenceSummary?: string[]
  sourceFinding?: string | null
  findingCount?: number | null
  maxSeverity?: string | null
  ageDays?: number | null
  informationSystemId?: string | null
  assetId?: number | null
  recommendation?: { id: string; title: string; status: string } | null
  informationSystem?: { id: string; name: string; code?: string | null } | null
}

type RegisterMeta = {
  executiveOpenRisks?: number
  internalOperationalObservations?: number
  complianceGapObservations?: number
  openComplianceGaps?: number
  openComplianceGapsBreakdown?: { total?: number; byStandard?: Record<string, number> }
  totalOpenRisks?: number
  totalRiskRelatedItems?: number
  requestedStandardCode?: string | null
  appliedStandardCode?: string | null
  returnedStandards?: string[]
  scope?: { type?: string; standardCode?: string | null }
  counts?: { beforeScope?: number; afterScope?: number; rejectedByScope?: number }
}

function levelLabel(level: number): string {
  if (level >= 25) return 'Critical'
  if (level >= 16) return 'High'
  if (level >= 9) return 'Medium'
  return 'Low'
}

export default function ComplianceCenterRiskRegister() {
  const { t } = useI18n()
  const { token } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [overviewStandard, setOverviewStandard] = useState(() =>
    normalizeComplianceStandardCode(
      searchParams.get('standardCode') || localStorage.getItem('ccSelectedStandard'),
    ),
  )
  const scopedStandard = useMemo(
    () => normalizeComplianceScope(overviewStandard),
    [overviewStandard],
  )
  const [risks, setRisks] = useState<RiskRow[]>([])
  const [meta, setMeta] = useState<RegisterMeta | null>(null)
  const [impactByControl, setImpactByControl] = useState<Record<string, { standardsAffected?: number; totalReadinessGain?: number; impacts?: Array<{ standardCode?: string }> }>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [controlKeyFilter, setControlKeyFilter] = useState(searchParams.get('controlKey') || '')
  const [registerView, setRegisterView] = useState<'executive' | 'observations' | 'all'>(
    (searchParams.get('registerView') as 'executive' | 'observations' | 'all') || 'executive',
  )

  useEffect(() => {
    const nextView = searchParams.get('registerView')
    if (nextView === 'executive' || nextView === 'observations' || nextView === 'all') {
      setRegisterView(nextView)
    }
    const nextControl = searchParams.get('controlKey') || ''
    setControlKeyFilter(nextControl)
    const nextStatus = searchParams.get('status') || ''
    setStatusFilter(nextStatus)
    const nextStd = searchParams.get('standardCode')
    if (nextStd) setOverviewStandard(normalizeComplianceStandardCode(nextStd))
  }, [searchParams])

  useEffect(() => {
    const syncFromStorage = () => {
      if (searchParams.get('standardCode')) return
      setOverviewStandard(normalizeComplianceStandardCode(localStorage.getItem('ccSelectedStandard')))
    }
    const onCustom = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail
      const next = normalizeComplianceStandardCode(detail ?? localStorage.getItem('ccSelectedStandard'))
      setOverviewStandard(next)
    }
    syncFromStorage()
    window.addEventListener('storage', syncFromStorage)
    window.addEventListener('cc:selected-standard', onCustom)
    return () => {
      window.removeEventListener('storage', syncFromStorage)
      window.removeEventListener('cc:selected-standard', onCustom)
    }
  }, [searchParams])

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      params.set('registerView', registerView)
      if (scopedStandard) params.set('standardCode', scopedStandard)
      if (statusFilter) params.set('status', statusFilter)
      const url = `${API_ENDPOINTS.GOVERNANCE_RISKS}?${params.toString()}`
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!res.ok) throw new Error('Failed to load risks')
      const json = await res.json()
      setRisks(Array.isArray(json?.risks) ? json.risks : [])
      setMeta(json?.meta || null)
      try {
        const summaryQuery = scopedStandard
          ? `?standardCode=${encodeURIComponent(scopedStandard)}&includeDiagnostics=true`
          : '?includeDiagnostics=true'
        const summaryRes = await fetch(`${API_ENDPOINTS.GRC_EXECUTIVE_SUMMARY}${summaryQuery}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (summaryRes.ok) {
          const summaryJson = await summaryRes.json()
          setImpactByControl(summaryJson?.data?.crossStandardImpactSummary?.impactByControl || {})
        }
      } catch {
        setImpactByControl({})
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load risks')
      setRisks([])
      setMeta(null)
    } finally {
      setLoading(false)
    }
  }, [token, statusFilter, registerView, scopedStandard])

  useEffect(() => {
    void load()
  }, [load])

  const openCount = useMemo(
    () => risks.filter((r) => !['Closed', 'CLOSED', 'Accepted', 'ACCEPTED'].includes(String(r.status))).length,
    [risks],
  )

  const displayedRisks = useMemo(() => {
    if (!controlKeyFilter.trim()) return risks
    const want = controlKeyFilter.trim().toUpperCase()
    return risks.filter((r) => String(r.controlKey || '').toUpperCase() === want)
  }, [risks, controlKeyFilter])

  // Keep durable URL filters in sync (survive refresh)
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    next.set('registerView', registerView)
    if (statusFilter) next.set('status', statusFilter)
    else next.delete('status')
    if (controlKeyFilter) next.set('controlKey', controlKeyFilter)
    else next.delete('controlKey')
    if (scopedStandard) next.set('standardCode', scopedStandard)
    const cur = searchParams.toString()
    const nxt = next.toString()
    if (cur !== nxt) setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerView, statusFilter, controlKeyFilter, scopedStandard])

  const executiveCount = meta?.executiveOpenRisks ?? 0
  const internalObservations =
    meta?.internalOperationalObservations ?? meta?.complianceGapObservations ?? 0
  const openGaps = meta?.openComplianceGaps ?? meta?.openComplianceGapsBreakdown?.total ?? 0

  const summaryText = useMemo(() => {
    if (registerView === 'executive') {
      const gapHint = openGaps > 0
        ? t('complianceCenterUi.riskRegister.gapHint', ' · {{count}} open compliance gaps', { count: openGaps })
        : ''
      return t(
        'complianceCenterUi.riskRegister.executiveSummary',
        '{{executive}} executive risks{{gapHint}}',
        { executive: executiveCount, gapHint },
      )
    }
    if (registerView === 'observations') {
      return t(
        'complianceCenterUi.riskRegister.observationsSummary',
        '{{count}} internal operational observations',
        { count: openCount || internalObservations },
      )
    }
    return t(
      'complianceCenterUi.riskRegister.allSummary',
      '{{executive}} executive risks · {{observations}} internal operational observations',
      { executive: executiveCount, observations: internalObservations },
    )
  }, [registerView, t, executiveCount, internalObservations, openCount, openGaps])

  const observationsViewHint =
    registerView === 'observations'
      ? t(
          'complianceCenterUi.riskRegister.observationsHint',
          'Internal operational observations — not executive risks.',
        )
      : null

  return (
    <PageLayout
      title={t('complianceCenterUi.riskRegister.title', 'Risk Register')}
      subtitle={t(
        'complianceCenterUi.riskRegister.subtitle',
        'Assessment-derived and governance risks linked to standards, controls, and recommendations.',
      )}
    >
      <GovernanceListingSection
        title={t('complianceCenterUi.riskRegister.listTitle', 'Open risks')}
        description={summaryText}
        actions={
          <>
            <select
              className={listStyles.filterSelect}
              value={registerView}
              onChange={(e) => setRegisterView(e.target.value as 'executive' | 'observations' | 'all')}
              aria-label={t('complianceCenterUi.riskRegister.viewFilter', 'Register view')}
            >
              <option value="executive">
                {t('complianceCenterUi.riskRegister.viewExecutive', 'Executive risks')}
              </option>
              <option value="observations">
                {t('complianceCenterUi.riskRegister.viewObservations', 'Compliance observations')}
              </option>
              <option value="all">
                {t('complianceCenterUi.riskRegister.viewAll', 'All')}
              </option>
            </select>
            <select
              className={listStyles.filterSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label={t('complianceCenterUi.riskRegister.statusFilter', 'Status filter')}
            >
              <option value="">{t('common.all', 'All')}</option>
              <option value="Open">Open</option>
              <option value="Mitigating">Mitigating</option>
              <option value="Closed">Closed</option>
            </select>
          </>
        }
      >
        {scopedStandard ? (
          <p className={listStyles.emptyText} data-testid="risk-register-scope">
            {t('complianceCenterUi.riskRegister.scopedTo', 'Scoped to {{standard}}', { standard: scopedStandard })}
          </p>
        ) : (
          <p className={listStyles.emptyText} data-testid="risk-register-scope">
            {t('complianceCenterUi.riskRegister.portfolioScope', 'Portfolio · All standards')}
          </p>
        )}
        {error ? <p className={listStyles.errorText}>{error}</p> : null}
        {observationsViewHint ? <p className={listStyles.emptyText}>{observationsViewHint}</p> : null}
        {!loading && registerView === 'executive' && displayedRisks.length === 0 && internalObservations > 0 ? (
          <p className={listStyles.emptyText}>
            {t(
              'complianceCenterUi.riskRegister.executiveEmpty',
              'No executive risks found. {{count}} internal operational observations are available in the Compliance observations view.',
              { count: internalObservations },
            )}
          </p>
        ) : null}
        {controlKeyFilter ? (
          <p className={listStyles.emptyText} data-testid="risk-register-control-filter">
            {t('complianceCenterUi.riskRegister.filteredByControl', 'Filtered by control {{control}}', {
              control: controlKeyFilter,
            })}
          </p>
        ) : null}
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th>{t('complianceCenterUi.riskRegister.colRisk', 'Risk')}</th>
                <th>{t('complianceCenterUi.riskRegister.colLevel', 'Level')}</th>
                <th>{t('complianceCenterUi.riskRegister.colStandard', 'Standard')}</th>
                <th>{t('complianceCenterUi.riskRegister.colControl', 'Control')}</th>
                <th>{t('complianceCenterUi.riskRegister.colOrigin', 'Origin')}</th>
                <th>{t('complianceCenterUi.riskRegister.colStatus', 'Status')}</th>
                <th>{t('complianceCenterUi.riskRegister.colOwner', 'Owner')}</th>
                <th>{t('complianceCenterUi.riskRegister.colRecommendation', 'Recommendation')}</th>
                <th>{t('complianceCenterUi.riskRegister.colCrossImpact', 'Cross-framework impact')}</th>
                <th>{t('complianceCenterUi.riskRegister.colSystem', 'Information system')}</th>
                <th>{t('complianceCenterUi.riskRegister.colAsset', 'Asset')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <TableLoadingRow colSpan={11} /> : null}
              {!loading && displayedRisks.length === 0 ? <TableEmptyRow colSpan={11} /> : null}
              {!loading
                ? displayedRisks.map((risk) => (
                    <tr key={risk.id}>
                      <td className={listStyles.td}>
                        <div>{risk.title}</div>
                        {risk.vulnerabilityCause ? (
                          <div style={{ fontSize: 12, opacity: 0.85 }}>{risk.vulnerabilityCause}</div>
                        ) : null}
                        {risk.findingCount != null && risk.findingCount > 0 ? (
                          <div style={{ fontSize: 12, opacity: 0.75 }}>
                            {t('complianceCenterUi.riskRegister.findingsAggregate', 'Findings')}: {risk.findingCount} open
                            {risk.maxSeverity ? ` · max ${risk.maxSeverity}` : ''}
                            {risk.ageDays != null ? ` · first observed ${risk.ageDays}d ago` : ''}
                          </div>
                        ) : risk.sourceFinding ? (
                          <div style={{ fontSize: 12, opacity: 0.75 }}>
                            {t('complianceCenterUi.riskRegister.findingsAggregate', 'Findings')}: {risk.sourceFinding}
                          </div>
                        ) : null}
                        {Array.isArray(risk.requiredEvidenceSummary) && risk.requiredEvidenceSummary.length > 0 ? (
                          <div style={{ fontSize: 12, opacity: 0.75 }}>
                            {t('complianceCenterUi.riskRegister.requiredEvidence', 'Required evidence')}: {risk.requiredEvidenceSummary.join('; ')}
                          </div>
                        ) : null}
                      </td>
                      <td className={listStyles.td}>{levelLabel(risk.level)}</td>
                      <td className={listStyles.td}>{risk.standardCode || '—'}</td>
                      <td className={listStyles.td}>{risk.controlKey || '—'}</td>
                      <td className={listStyles.td}>
                        {risk.riskOrigin === 'VULNERABILITY_DRIFT'
                          ? t('complianceCenterUi.riskRegister.vulnerabilityDrift', 'Vulnerability-driven compliance drift')
                          : risk.riskOrigin || '—'}
                      </td>
                      <td className={listStyles.td}>{risk.status}</td>
                      <td className={listStyles.td}>{risk.ownerName || '—'}</td>
                      <td className={listStyles.td}>
                        {risk.recommendation ? (
                          <Link to="/compliance-center/remediation">{risk.recommendation.title}</Link>
                        ) : risk.recommendationLinkageReason ? (
                          <span title={risk.recommendationLinkageReason}>
                            {t(`complianceCenterUi.riskRegister.linkage.${risk.recommendationLinkageReason}`, risk.recommendationLinkageReason)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={listStyles.td}>
                        {risk.controlKey && impactByControl[risk.controlKey]
                          ? (impactByControl[risk.controlKey].impacts || [])
                              .filter((row) => row.standardCode && row.standardCode !== risk.standardCode)
                              .map((row) => row.standardCode)
                              .join(', ') || '—'
                          : '—'}
                      </td>
                      <td className={listStyles.td}>
                        {risk.informationSystem?.name || risk.informationSystemId || '—'}
                      </td>
                      <td className={listStyles.td}>{risk.assetId != null ? `#${risk.assetId}` : '—'}</td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
      </GovernanceListingSection>
    </PageLayout>
  )
}
